/* eslint-disable indent */
import classNames from 'classnames';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import Box from '../../components/box/index.jsx';
import { PackageAiBtn2 as Btn } from '../../components/btn/index.jsx';
import Compare from '../../components/compare/compare.jsx';
import { ModalContext } from '../../components/context/context.jsx';
import Filter from '../../components/filter/index.jsx';
import ModalGreen from '../../components/modal/modal.jsx';
import Perceived from '../../components/perceived/perceived.jsx';
import TopBar from '../../components/top-bar/top.jsx';
import Topline from '../../components/topline/topline.jsx';
import style from './main.module.scss';

const MODAL_DATA_1 = [
  {
    title: 'Brand Logo Visibility',
    key: 'brand_logo_visibility',
    titleTip:
      'This metric measures the probability of the brand logo being noticed in the first 3-5 seconds. It is based on 3M VAS algorithm.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Variant Name Visibility',
    key: 'variant_name_visibility',
    titleTip:
      'This metric evaluates the likelihood that the variant name will be noticed within the first 3-5 seconds of viewing. It utilizes the 3M VAS algorithm to provide this assessment.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Considered Appeal',
    key: 'appeal',
    titleTip:
      'This metric measures the percentage of respondents who find the packaging moderately and strongly appealing. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Intuitive appeal',
    key: 'intuitive_appeal',
    titleTip:
      'This metric measures the percentage of respondents who find the somewhat or very willing to purchase the product based on design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Unpriced Purchase Intent',
    key: 'upi',
    titleTip:
      'This metric measures the percentage of respondents who find the somewhat or very willing to purchase the product based on design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
];

const MODAL_DATA_2 = [
  {
    title: 'Taste',
    key: 'taste',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly believe the product would have a delicious taste based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'High Quality',
    key: 'premium',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product is of high quality based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Healthy',
    key: 'natural',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product is a healthy choice based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Premium',
    key: 'value',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product would be worth paying more for based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Sustainable',
    key: 'sustainable',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product is a more environmentally friendly choice based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Modernity',
    key: 'modernity',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product looks modern based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Excitement',
    key: 'excitement',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product looks exciting based on its design alone. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
  {
    title: 'Desirability',
    key: 'desirability',
    titleTip:
      'This metric measures the percentage of respondents who somewhat or strongly think that the product looks like something that they would want to have. It is derived from consumer survey data.',
    score: 0,
    avg: 0,
  },
];

function SwitchBtn({ className, value, setValue }) {
  const onTable = useCallback(() => {
    setValue('table');
  }, [setValue]);
  const onChart = useCallback(() => {
    setValue('chart');
  }, [setValue]);

  if (value === 'table') {
    return (
      <Btn className={className} onClick={onChart}>
        Switch to chart
      </Btn>
    );
  } else if (value === 'chart') {
    return (
      <Btn className={className} onClick={onTable}>
        Back to table
      </Btn>
    );
  } else {
    return <div className={className}>Unknown tab {value}</div>;
  }
}

const PackageAiView = () => {
  // tab list
  const mainTabList = [
    { title: 'Topline Metrics' },
    { title: 'Perceived Benefits Deepdive' },
    { title: 'Compare' },
  ];

  // main tab
  const [mainTab, setMainTab] = useState(0);
  const changeMainTab = (index) => {
    setMainTab(index);
    setModalType(index);
    if (index === 2) {
      // reset compare step
      setCompareStep(0);
    }
  };

  // compare step
  const [compareStep, setCompareStep] = useState(0);

  const isShowFilter = useMemo(() => {
    if (mainTab !== 2) return true;
    else if (compareStep === 0) return true;
    else return false;
  }, [mainTab, compareStep]);
  const isShowSwitch = useMemo(() => {
    return mainTab !== 2;
  }, [mainTab]);

  // Modal
  const [modalType, setModalType] = useState(0);
  const { isOpen, modalData, closeModal, avgs, getCategoryRank } =
    useContext(ModalContext);

  const currentModalData = useMemo(() => {
    let md;

    if (modalType === 0) {
      md = MODAL_DATA_1;
    } else if (modalType === 1) {
      md = MODAL_DATA_2;
    } else {
      console.warn('Unknown modal type', modalType);
      return null;
    }

    let isPercent = true;
    let max = 100.0;

    const buildFlag = (key) => {
      const rank = getCategoryRank(
        modalData.category,
        key,
        modalData.market,
        modalData.sn
      );
      // console.log('key', key, rank);
      if (rank <= 0.3) return 'good';
      else if (rank <= 0.7) return 'normal';
      else return 'bad';
    };

    const data = md.map((item) => {
      return {
        ...item,
        score: modalData[item.key],
        flag: buildFlag(item.key),
      };
    });

    return {
      title: modalData.name,
      id: modalData.sn,
      market: modalData.market,
      cover: modalData.cover,
      table: {
        title: 'Metric',
        barContent: 'Score',
        body: data,
      },
      isPercent: isPercent,
      max: max,
    };
  }, [modalData, getCategoryRank]);

  // filters
  const [filterData, setFilterData] = useState({});
  const [subFilterData, setSubFilterData] = useState({});
  const [search, setSearch] = useState();

  // switch tab
  const [tab, setTab] = useState('table');

  // main content
  const mainContet = useMemo(() => {
    switch (mainTab) {
      case 0:
        return (
          <Topline
            tab={tab}
            filterData={filterData}
            subFilterData={subFilterData}
            getCategoryRank={getCategoryRank}
          />
        );
      case 1:
        return (
          <Perceived
            tab={tab}
            filterData={filterData}
            subFilterData={subFilterData}
            getCategoryRank={getCategoryRank}
          />
        );
      case 2:
        return (
          <Compare
            step={compareStep}
            setStep={setCompareStep}
            search={search}
            filterData={filterData}
            getCategoryRank={getCategoryRank}
          />
        );
      default:
        return <div>Unknown tab {mainTab}</div>;
    }
  });

  return (
    <div className={style.fixedContent}>
      <TopBar />
      <Box className={style.contentWrap}>
        <div className={style.contentInner}>
          <div className={style.tabBar}>
            {mainTabList.map((tab, index) => {
              return (
                <div
                  className={classNames({
                    [style.firstTab]: index === 0,
                    [style.secondTab]: index === 1,
                    [style.thirdTab]: index === 2,
                    [style.active]: index === mainTab,
                  })}
                  onClick={() => {
                    changeMainTab(index);
                  }}
                  key={index}
                >
                  <div className={style.outerCircle}>
                    <div className={style.innerCircle}></div>
                  </div>
                  {index === 1 && <div className={style.lineRight}></div>}
                  {index === 1 && <div className={style.lineLeft}></div>}
                  <span className={style.navTitle}>{tab.title}</span>
                </div>
              );
            })}
          </div>
          <div className={style.tabContent}>
            <div className={style.optWrap}>
              {isShowFilter && (
                <div className={style.box11}>
                  {isShowSwitch && (
                    <div className={style.box111}>
                      <SwitchBtn value={tab} setValue={setTab} />
                    </div>
                  )}
                  <div className={style.box112}>
                    <Filter
                      className={style.box1121}
                      showSearch={mainTab === 2}
                      search={search}
                      setSearch={setSearch}
                      initData={filterData}
                      setData={setFilterData}
                      showSubFilters={mainTab !== 2}
                      subData={subFilterData}
                      setSubData={setSubFilterData}
                    />
                  </div>
                </div>
              )}
              {mainContet}
            </div>
          </div>
        </div>
      </Box>
      {/* Modal */}
      <ModalGreen
        open={isOpen}
        data={currentModalData}
        avgs={avgs}
        modalType={modalType}
        closeModal={closeModal}
      />
    </div>
  );
};

export default PackageAiView;
